import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Collapse } from '@mui/material';
import classNames from 'classnames';
import { MenuItems } from './menuItems';
import HLDLogo from '../assets/images/DataViewLogo.png';
import FooterLogoSmall from '../assets/images/SidebarLogoFooterSmall.svg';
import { useLogin } from '../hooks/useLogin';
import { CollapsIcon } from '../assets/icons';
import { Tooltip } from '@mui/material';
interface SubMenuItem {
  id: number;
  label: string;
  link: string;
}

interface MenuItem {
  id: number;
  label: string;
  icon: React.ReactElement;
  activeIcon: React.ReactElement;
  link: string;
  hasSubmenu?: boolean;
  subMenu?: SubMenuItem[];
}

interface RoleMenuFeature {
  menuFeatureId: number;
  menuFeature: string;
  hasPermission: boolean;
  isSubMenu: boolean;
}

interface RoleMenu {
  menuId: number;
  menuName: string;
  menuFeatures: RoleMenuFeature[];
}

interface Role {
  roleID: number;
  roleName: string;
  menu: RoleMenu[];
}

const Sidebar: React.FC<{ isCollapsed: boolean; setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>> }> = ({ isCollapsed, setIsCollapsed }) =>  {
  // const [isCollapsed, setIsCollapsed] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
  const [activeSubmenuItem, setActiveSubmenuItem] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [roleData, setRoleData] = useState<Role[]>([]);
  const location = useLocation();
  const { pathname } = location;
  const { handleAuthenticationFailure } = useLogin();
  
  useEffect(() => {
    const storedIsCollapsed = localStorage.getItem('isCollapsed');
    if (storedIsCollapsed !== null) {
      setIsCollapsed(storedIsCollapsed === 'true'); // Convert string to boolean
    }

  }, [setIsCollapsed]);
  useEffect(() => {
    void handleAuthenticationFailure();
  }, [handleAuthenticationFailure]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleResize = (): void => {
      const isSmallScreen = window.innerWidth < 1200;
      setIsMobile(isSmallScreen);
      setIsCollapsed(isSmallScreen);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsCollapsed]);

  useEffect(() => {
    const storedRole = localStorage.getItem('role');
    if (storedRole !== null && storedRole !== '') {
      try {
        const parsedRole = JSON.parse(storedRole) as Role[];
        setRoleData(parsedRole);
      } catch (error) {
        console.error('Error parsing role data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const activeSubItem = MenuItems.flatMap((item) => item.subMenu ?? []).find(
      (subItem) => pathname.includes(subItem.link.toLowerCase())
    );

    if (activeSubItem) {
      setActiveSubmenuItem(activeSubItem.link);
      const parentMenu = MenuItems.find((item) =>
        item.subMenu?.some((sub) => sub.link === activeSubItem.link)
      );
      if (parentMenu) {
        setOpenSubmenu(parentMenu.id);
      }
    } else {
      const parentMenu = MenuItems.find((item) =>
        pathname.includes(item.link.toLowerCase())
      );
      if (parentMenu) {
        setOpenSubmenu(parentMenu.id);
        setActiveSubmenuItem(null);
      } else {
        setOpenSubmenu(null);
        setActiveSubmenuItem(null);
      }
    }
  }, [pathname]);

  
  const handleSidebarToggle = useCallback((): void => {
    setIsCollapsed((prev) => {
      const newCollapsedState = !prev;
      localStorage.setItem('isCollapsed', newCollapsedState.toString());
      return newCollapsedState;
    });
  }, [setIsCollapsed]);

  const handleSubmenuToggle = useCallback((id: number): void => {
    setOpenSubmenu((prev) => (prev === id ? null : id));
    setIsCollapsed(false);
    localStorage.setItem('isCollapsed', 'false');
  }, []);

  const renderArrowIcon = useCallback(
    (isOpen: boolean): JSX.Element => (
      <svg
        className={classNames('ml-auto transition-transform duration-200', {
          'transform rotate-180': isOpen,
        })}
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.33325 4.66675L5.99992 8.33341L9.66659 4.66675"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    []
  );

  const shouldShowMenuItem = useCallback((menuLabel: string): boolean => {
    
    return roleData.length > 0 && roleData[0]?.menu.some(menu => menu.menuName.toLowerCase() === menuLabel.toLowerCase());
  }, [roleData]);

  // New function to check submenu permissions
  const shouldShowSubMenuItem = useCallback((subMenuLabel: string, menuLabel: string): boolean => {
    const roleMenu = roleData[0]?.menu.find(menu => menu.menuName.toLowerCase() === menuLabel.toLowerCase());
    console.log(roleMenu);
    if (!roleMenu) return false;
    const subMenuFeature = roleMenu.menuFeatures.find(feature => feature.menuFeature.toLowerCase() === subMenuLabel.toLowerCase() && feature.isSubMenu && feature.hasPermission);
    console.log(subMenuFeature);

    return subMenuFeature !== undefined;
  }, [roleData]);

  const renderMenuItem = useCallback(
    (menu: MenuItem) => {
      if (!shouldShowMenuItem(menu.label)) return null;

      const isActive = pathname.includes(menu.link.toLowerCase());

      const iconStyle = {
        width: '18px',
        height: '18px',
        marginTop: '-5px',
        marginLeft: isCollapsed ? '0px' : '-8px',
        marginRight: isCollapsed ? '10px' : '5px',
      };

      return (
        <li key={menu.id} className="py-2">          
          {menu.hasSubmenu === false ? (
              <Tooltip
              title={!isCollapsed ? '' : menu.label} 
              placement="right"
              arrow
              classes={{
                tooltip: 'custom-tooltip',
                arrow: 'custom-arrow',
              }}
            >
            <Link
              to={menu.link}
              className={classNames('menu-link py-2 flex items-center', {
                active: isActive,
              })}
              onClick={() => setIsCollapsed(false)}
            >
              <div className="flex items-center gap-2">
                <div style={iconStyle}>
                  {isActive ? menu.activeIcon : menu.icon}
                </div>
                {!isCollapsed && <span>{menu.label}</span>}
              </div>
            </Link>
            </Tooltip>
          ) : (
            <>
              <div
                className={classNames('menu-link py-2 cursor-pointer flex items-center justify-between', {
                  active: isActive,
                })}
                onClick={() => handleSubmenuToggle(menu.id)}
              >
                <Tooltip
              title={!isCollapsed ? '' : menu.label} // Tooltip only in collapsed mode
              placement="right"
              arrow
              classes={{
                tooltip: 'custom-tooltip',
                arrow: 'custom-arrow',
              }}
            >
                <div className="flex items-center gap-2">
                  <div style={iconStyle}>
                    {isActive ? menu.activeIcon : menu.icon}
                  </div>
                  {!isCollapsed && <span>{menu.label}</span>}
                </div>
                </Tooltip>
                {!isCollapsed && renderArrowIcon(openSubmenu === menu.id)}
              </div>
              {!isCollapsed && (
                <Collapse in={openSubmenu === menu.id} timeout="auto" unmountOnExit>
                  <ul className="pl-4 mt-2 submenu">
                    {menu?.subMenu?.map((subItem) => (
                      shouldShowSubMenuItem(subItem.label, menu.label) && (
                        <li key={subItem.id} className="pt-1 pb-1 flex submenu-item">
                          <Link
                            to={subItem.link}
                            className={classNames('menu-link py-1 flex items-center', {
                              active: activeSubmenuItem === subItem.link,
                            })}
                            onClick={() => setIsCollapsed(false)}
                          >
                            <span className="menu-link-text">{subItem.label}</span>
                          </Link>
                        </li>
                      )
                    ))}
                  </ul>
                </Collapse>
              )}
            </>
          )}
         
        </li>
      );
    },
    [pathname, activeSubmenuItem, openSubmenu, isCollapsed, handleSubmenuToggle, renderArrowIcon, shouldShowMenuItem, shouldShowSubMenuItem]
  );

  const wrapperClasses = classNames(
    "bg-white flex justify-between flex-col",
    {
      ["max-w-[230px]"]: !isCollapsed && !isMobile,
      ["min-w-[80px]"]: isCollapsed || isMobile,
    }
  );

  const collapseIconClasses = classNames(
    'rounded bg-secondary absolute right-0 ',
    {
      'rotate-180 collapsed': isCollapsed,
    }
  );

  return (
    <div className={wrapperClasses} style={{ transition: 'width 300ms cubic-bezier(0.2, 0, 0, 1)' }}>
      <div className="flex flex-col">
        <div className="flex items-center justify-between relative h-[60px]">
          <div className="flex items-center gap-4" style={{ margin: 'auto', padding: '10px' }}>
            <img
              src={isCollapsed ? FooterLogoSmall : HLDLogo}
              alt="logo"
              width={isCollapsed ? 50 : 120}
              height={66}
              className={`mx-auto ${isCollapsed ? 'w-[30px] h-[40px]' : 'w-[180px] h-[66px]'}`}
            />
          </div>
          <button className={collapseIconClasses} onClick={handleSidebarToggle} aria-label="Toggle sidebar">
            <CollapsIcon />
          </button>
        </div>

        <div className="flex flex-col items-center mt-5 px-4 overflow-y-auto overflow-x-hidden">
          <ul className={classNames('menu sidebar-menu-container', { 'w-60': !isCollapsed })}>
            {MenuItems.map(renderMenuItem)}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
